const { devicesToProcess } = require('./util/device')
const automaticReports = require('./automaticReports')
const traccarHelper = require('./util/traccar')
const { getDates, convertMS, getTranslations, convertFromUTC } = require('./util/utils')
const { getStyle } = require('./reportStyle')
const { getUserPartner } = require('fleetmap-partners')
const { addTable, headerFromUser } = require('./util/pdfDocument')
const jsPDF = require('jspdf')
require('jspdf-autotable')

const fileName = 'MachineReport'

async function createMachinesReport (from, to, userData, traccar) {
  console.log('Create MachineReport')
  const reportData = []

  console.log('ByDevice')
  const report = await createMachinesReportByDevice(from, to, userData, traccar)
  reportData.push(report)

  return reportData
}

async function createMachinesReportByDevice (from, to, userData, traccar) {
  const devices = devicesToProcess(userData)

  const allData = {
    devices: [],
    from,
    to,
    xpert: devices.filter(d => d.attributes.xpert).length > 0
  }

  const sliced = automaticReports.sliceArray(devices, 5)

  let deviceCount = 0
  for (const slice of sliced) {
    const allInOne = await traccarHelper.getAllInOne(traccar, from, to, slice, true, false, false, false, deviceCount, devices.length)
    const routeData = allInOne.route

    if (routeData.length > 0) {
      allData.devices.push(...processDevices(from, to, slice, {
        route: routeData
      }, userData))
    }
    deviceCount = deviceCount + slice.length
  }

  return allData
}

function processDevices (from, to, devices, data, userData) {
  const devicesResult = []

  devices.forEach(d => {
    const deviceRoute = data.route.filter(t => t.deviceId === d.id)

    if (deviceRoute.filter(p => p.attributes.ignition && p.valid).length > 0) {
      const summary = []

      const convertFrom = new Date(convertFromUTC(from, userData.user.attributes.timezone))
      const convertTo = new Date(convertFromUTC(to, userData.user.attributes.timezone))

      if (userData.withDetails) {
        const ignitions = calculateIgnitions(deviceRoute)
        for (const ignition of ignitions) {
          const dayData = getSummaryData(new Date(ignition.on.fixTime), d, ignition.route, ignition, undefined)
          summary.push(dayData)
        }
      } else {
        const dates = getDates(convertFrom, convertTo)
        for (const date of dates) {
          const fromByDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
          const toByDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59)

          const routeByDay = deviceRoute.filter(p => (new Date(p.fixTime) > fromByDay && new Date(p.fixTime) < toByDay) && p.valid)
          routeByDay.sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime())

          const ignitions = calculateIgnitions(routeByDay)
          const dayData = getSummaryData(date, d, routeByDay, undefined, ignitions)
          summary.push(dayData)
        }
      }
      if (summary.length) {
        const deviceData = {
          device: d,
          summary
        }
        devicesResult.push(deviceData)
      }
    }
  })

  return devicesResult
}

function getSummaryData (date, device, route, ignition, ignitions) {
  const startTime = ignition ? ignition.on.fixTime : (ignitions.length ? ignitions[0].on.fixTime : undefined)
  const endTime = ignition ? ignition.off.fixTime : (ignitions.length ? ignitions[ignitions.length - 1].off.fixTime : undefined)
  let engineHours = ignition
    ? ignition.route[ignition.route.length - 1].attributes.hours - ignition.route[0].attributes.hours
    : (ignitions ? ignitions.reduce((a, b) => a + (b.route[b.route.length - 1].attributes.hours - b.route[0].attributes.hours), 0) : undefined)

  if (!engineHours) {
    engineHours = 0
    if (ignition) {
      engineHours = new Date(endTime).getTime() - new Date(startTime).getTime()
    } else {
      ignitions.forEach(e => {
        engineHours = engineHours + (new Date(e.off.fixTime).getTime() - new Date(e.on.fixTime).getTime())
      })
    }
  }

  return {
    date,
    deviceId: device.id,
    engineHours,
    totalHours: route.length ? route[route.length - 1].attributes.hours : undefined,
    startTime,
    endTime
  }
}

function calculateIgnitions (routeOfDay) {
  let indexStart
  let last
  const ignitions = []
  routeOfDay.forEach((p, index) => {
    // difference between last position and current position is bigger than 5 minutes
    if ((last && last.attributes.ignition && (new Date(p.fixTime).getTime() - new Date(last.fixTime).getTime()) > 300000) &&
        ignitions.length &&
        !ignitions[ignitions.length - 1].off) {
      ignitions[ignitions.length - 1].off = last
      ignitions[ignitions.length - 1].route = routeOfDay.slice(indexStart, index)
    }

    if (p.attributes.ignition && (!ignitions.length || ignitions[ignitions.length - 1].off)) {
      ignitions.push({ on: p })
      indexStart = index
    }

    // ignition is off
    // or is last route position
    if ((!p.attributes.ignition || index + 1 === routeOfDay.length) &&
        ignitions.length &&
        !ignitions[ignitions.length - 1].off) {
      ignitions[ignitions.length - 1].off = p
      ignitions[ignitions.length - 1].route = (index + 1) === routeOfDay.length ? routeOfDay.slice(indexStart) : routeOfDay.slice(indexStart, index + 1)
    }

    last = p
  })

  return ignitions
}

async function exportMachinesReportToPDF (userData, reportData) {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF.jsPDF('l')
  const translations = getTranslations(userData)
  await headerFromUser(doc, translations.report.titleMachinesReport, userData.user)

  console.log('Export to PDF')
  const headers = [translations.report.date,
    translations.report.start,
    translations.report.end,
    translations.report.engineHours,
    translations.report.totalEngineHours]

  const weekDays = [
    translations.report.sunday,
    translations.report.monday,
    translations.report.tuesday,
    translations.report.wednesday,
    translations.report.thursday,
    translations.report.friday,
    translations.report.saturday
  ]

  reportData.devices.forEach(function (d, index) {
    const name = userData.byDriver ? d.driver.name : d.device.name
    const group = userData.byDriver ? userData.groups.find(g => g.drivers.includes(d.id)) : userData.groups.find(g => d.device.groupId === g.id)

    const space = index === 0 ? 8 : 0
    if (index) {
      doc.addPage()
    }

    doc.setFontSize(13)
    doc.text(name, 20, space + 20)
    doc.setFontSize(11)
    doc.text(group ? translations.report.group + ': ' + group.name : '', 150, space + 20)
    doc.text(new Date(reportData.from).toLocaleString() + ' - ' + new Date(reportData.to).toLocaleString(), 20, space + 25)

    const data = []
    d.summary.forEach(s => {
      const temp = [
        new Date(s.date).toLocaleDateString() + ' - ' + weekDays[s.date.getDay()],
        s.startTime ? new Date(s.startTime).toLocaleTimeString() : '',
        s.endTime ? new Date(s.endTime).toLocaleTimeString() : '',
        convertMS(s.engineHours),
        s.totalHours ? convertMS(s.totalHours) : ''
      ]

      data.push(temp)
    })

    const footValues = ['',
      '', '',
      convertMS(d.summary.reduce((a, b) => a + (b.engineHours || 0), 0))]

    const style = getStyle(getUserPartner(userData.user))
    addTable(doc, headers, data, footValues, style, 40)
  })

  return doc
}

async function exportMachinesReportToExcel (userData, reportData) {
  console.log('Export to Excel')
  const translations = getTranslations(userData)

  const settings = {
    sheetName: translations.report.titleMachinesReport, // The name of the sheet
    fileName // The name of the spreadsheet
  }

  const weekDays = [
    translations.report.sunday,
    translations.report.monday,
    translations.report.tuesday,
    translations.report.wednesday,
    translations.report.thursday,
    translations.report.friday,
    translations.report.saturday
  ]

  const headers = [{ label: translations.report.vehicle, value: 'name' },
    { label: translations.report.group, value: 'group' },
    { label: translations.report.date, value: 'date' },
    { label: translations.report.start, value: 'start' },
    { label: translations.report.end, value: 'end' },
    { label: translations.report.engineHours, value: 'engineHours' },
    { label: translations.report.totalEngineHours, value: 'totalEngineHours' }]

  let data = []
  reportData.devices.forEach(d => {
    const group = userData.groups.find(g => d.device.groupId === g.id)

    data = data.concat([{}])
    data = data.concat(d.summary.map(s => {
      return {
        name: d.device.name,
        group: group ? group.name : '',
        date: new Date(s.date).toLocaleDateString(),
        weekday: weekDays[s.date.getDay()],
        start: s.startTime ? new Date(s.startTime).toLocaleTimeString() : '',
        end: s.endTime ? new Date(s.endTime).toLocaleTimeString() : '',
        engineHours: convertMS(s.engineHours),
        totalEngineHours: s.totalHours ? convertMS(s.totalHours) : ''
      }
    }))
  })
  console.log(data)
  return {
    headers,
    data,
    settings
  }
}

exports.createMachinesReport = createMachinesReport
exports.exportMachinesReportToPDF = exportMachinesReportToPDF
exports.exportMachinesReportToExcel = exportMachinesReportToExcel
exports.create = createMachinesReport
exports.exportToPDF = exportMachinesReportToPDF
exports.exportToExcel = exportMachinesReportToExcel
